import { TextMergeType } from "./TextMergeType";
import { TextMergeTestResult } from "./TextMergeTestResult";
import { RootTextElement } from "./Elements/Text/RootTextElement";
import { RuleExecutable } from "../Common/Execution/RuleExecutable";
import { DataUtilities } from "../Common/DataUtilities";
import { StateManager } from "../Common/StateManager";
import { ExecutionData } from "../Common/Data/ExecutionData/ExecutionData";
import { ITestingContext } from "../Common/Testing/ITestingContext";
import { EventLog } from "../Common/Event/EventLog";
import { SubstitutionTextElement } from "./Elements/SubstitutionTextElement";
import { IDataDefinition } from "../Common/Data/Definitions/IDataDefinition";
import { StringDataDefinition } from "../Common/Data/Definitions/StringDataDefinition";
import type { AppGlueAny } from "../Common/AppGlueAny";

export class TextMergeConfiguration extends RuleExecutable {
    static DEFAULT_VALUE_DATA_NAME: string = "__Default__Value__Merge__";
    static HIGHLIGHT_FIELDS_DATA_NAME: string = "__Highlight__Fields__";
    _id: string = DataUtilities.generateUniqueId();
    private _subsitution: boolean = false;

    private _defaultValue: string = "[Missing]";

    get defaultValue(): string {
        return this._defaultValue;
    }

    set defaultValue(value: string) {
        this._defaultValue = value;
        StateManager.propertyChanged(this, "defaultValue");
    }

    private _type: TextMergeType = TextMergeType.PlAIN;

    get type(): TextMergeType {
        return this._type;
    }

    set type(value: TextMergeType) {
        this._type = value;

        if (value === TextMergeType.XML || value === TextMergeType.JSON) this._defaultValue = "";
        StateManager.propertyChanged(this, "type");
    }

    private _rootElement: RootTextElement = new RootTextElement();

    get rootElement(): RootTextElement {
        this._rootElement.setInputData(() => {
            return this.getInputDefinition();
        });
        return this._rootElement;
    }

    set rootElement(value: RootTextElement) {
        this._rootElement = value;
    }

    get substitution(): boolean {
        return this._subsitution;
    }

    set substitution(value: boolean) {
        this._subsitution = value;
        if (this._subsitution) {
            if (this.rootElement.elements.length !== 1 || this.rootElement.elements[0].__type !== "substitution") {
                let executionData = new ExecutionData();
                executionData.set(TextMergeConfiguration.DEFAULT_VALUE_DATA_NAME, "[!Field!]");
                let oldText = this.run(executionData, undefined);
                let subElement = new SubstitutionTextElement();
                subElement.configuration.text = oldText;
                this.rootElement.removeAllElements();
                this.rootElement.addElement(subElement);
                subElement.parent = this.rootElement;
            }
        }
        StateManager.propertyChanged(this, "substitution");
    }

    get executionResult(): IDataDefinition | (() => IDataDefinition) {
        return this._executionResult!;
    }

    set executionResult(value: IDataDefinition | (() => IDataDefinition)) {
        let val = typeof value === "function" ? value() : value;

        if (!(val instanceof StringDataDefinition)) {
            throw new Error("can not set the executionResult with " + val.type);
        }

        this._executionResult = value;
    }

    run(data: ExecutionData, testContext: ITestingContext | undefined): AppGlueAny {
        let fieldData = new ExecutionData({ ...data.values });
        if (!fieldData.get(TextMergeConfiguration.DEFAULT_VALUE_DATA_NAME)) {
            fieldData.set(TextMergeConfiguration.DEFAULT_VALUE_DATA_NAME, this._defaultValue);
        }

        let subContext: TextMergeTestResult | undefined;
        if (testContext) {
            subContext = new TextMergeTestResult(this._id);
            subContext.definition = this;

            testContext.addTestResultItem(subContext);
            subContext.addLogItem(new EventLog("TextMerge: start evaluating Text Merge"));
        }

        try {
            let result = this._rootElement.output(fieldData, this, subContext);

            if (subContext) {
                fieldData.set(TextMergeConfiguration.HIGHLIGHT_FIELDS_DATA_NAME, true);

                // do not pass test context because do not want rules to show up a second time
                subContext.textWithFields = this._rootElement.output(fieldData, this, undefined);

                subContext.addLogItem(new EventLog(`TextMerge: end evaluating Text Merge with result [${result}]`));
                subContext.result = result;
            }

            return result;
        } catch (e: AppGlueAny) {
            subContext?.addLogItem(new EventLog("TextMerge: error merging text " + e.message));
        }
    }

    getStorageData(): object {
        let d = super.getStorageData();

        Reflect.set(d, "root", this._rootElement.getStorageData());
        Reflect.set(d, "raw", this._subsitution);
        Reflect.set(d, "id", this._id);

        return d;
    }

    setStorageData(data: object) {
        super.setStorageData(data);

        this._rootElement = new RootTextElement();
        this._rootElement.setStorageData(Reflect.get(data, "root"));
        this._subsitution = Reflect.get(data, "raw");
        this._id = Reflect.get(data, "id");
    }
}
